import { useEffect, useRef, useState } from "react";
import { useTranslation } from "../context/TranslationContext";
import Button from "./ds/Button";
import Modal from "./ds/Modal";

/**
 * A confirmation that asks for a few words: why a buyer wants a refund, or why a seller
 * turns one down. `minLength` 0 makes the words optional.
 *
 * Resolves through `onConfirm(text)`; the caller closes it when that succeeds.
 */
export default function ReasonModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  label,
  placeholder,
  confirmLabel,
  minLength = 0,
  maxLength = 1000,
  loading = false,
  variant = "default",
  isDark,
}) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [touched, setTouched] = useState(false);
  const fieldRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    setText("");
    setTouched(false);
    const focus = window.setTimeout(() => fieldRef.current?.focus(), 50);
    return () => window.clearTimeout(focus);
    // Reset only when it opens; `loading` and `onClose` don't reopen it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const tooShort = text.trim().length < minLength;

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (tooShort || loading) return;
    onConfirm?.(text.trim());
  };

  return (
    <Modal open onClose={() => !loading && onClose?.()} title={title} description={message} closeLabel={t("common.cancel")} isDark={isDark}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-space-8">
        <label className="text-footnote-semibold text-label-primary">
          {label}
          {minLength > 0 && <span className="text-status-danger"> *</span>}
        </label>
        <textarea
          ref={fieldRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={maxLength}
          rows={4}
          placeholder={placeholder}
          aria-invalid={touched && tooShort}
          className={`w-full resize-none rounded-[22px] bg-fill-tertiary px-space-16 py-space-12 text-callout text-label-primary outline-none placeholder:text-label-tertiary focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)] ${
            touched && tooShort ? "shadow-[0_0_0_1px_var(--color-status-danger)]" : ""
          }`}
        />
        <div className="flex items-start justify-between gap-space-12 text-caption-1">
          <span className="text-status-danger">{touched && tooShort ? t("refunds.reasonRequired") : ""}</span>
          <span className="text-label-secondary">
            {text.length}/{maxLength}
          </span>
        </div>

        <div className="mt-space-8 flex justify-end gap-space-8">
          <Button variant="gray" size="m" onClick={onClose} disabled={loading}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant={variant === "danger" ? "danger" : "filled"} size="m" loading={loading}>
            {confirmLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
